/* eslint-disable max-lines */
import { evidenceInputActiveAtom, linkedContextPreviewAtom, linkedPatientContextAtom, linkedSessionContextAtom, newChatModeAtom, scrollToUserMessageTriggerAtom, selectedDomainsAtom, setSelectedDomainsAtom, type LinkedPatientContext, type LinkedSessionContext } from '@/atoms/chatAtoms';
import { chatInputFocusTriggerAtom, pendingChatImmediateAtom, pendingChatMessageAtom, pendingChipMetaAtom, pendingEntryMethodAtom } from '@/atoms/chatLaunchAtoms';
import { sectionDictateStatusAtom } from '@/atoms/dictationAtom';
import {
  getErrorCode,
  getErrorMessage,
  getPreviewUrl,
  getUploadProgress,
  isErrorFile,
  isFileUploading,
  useChatFileUpload,
  type ChatUploadErrorCode,
  type ChatUploadFile,
} from '@/hooks/useChatFileUpload';
import { formatChatUploadErrorMessage } from '@/hooks/useChatFileUpload.messages';
import { useChatInputDraft } from '@/components/chat/hooks/useChatInputDraft';
import { useChatPaste } from '@/hooks/useChatPaste';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { ChatMode } from '@/components/chat/types/types';
import {
  IS_AUTO_MODE_ONLY_ENABLED,
  IS_CHAT_ATTACHMENTS_ENABLED,
} from '@/features/flags/legacy-flags';
import { finishTracking, startTracking, TrackingKey } from '@heidi/runtime/performance';
import { composeSelectedTextQuestionPrompt } from '@/lib/selection-ai-prompts';
import { ComposerAmbientGlow } from '@heidi/agents';
import { Button } from '@heidi/design-system/button';
import { FileCard } from '@heidi/design-system/file-card';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaptionBold,
  TypographyFootNote,
} from '@heidi/design-system/typography';
import { offlineGatingMessages } from '@/components/offline-recording/offline-gating-messages';
import { useOfflineGatedTooltip } from '@/components/offline-recording/use-offline-gated-tooltip';
import { SlideContextChip } from '@/components/slide-builder/SlideContextChip';
import { EditorType } from '@heidi/editor/core';
import BlockEditorMarkdown from '@/components/tiptap/vMarkdown/BlockEditor/block-editor';
import { SlashCommand } from '@/components/tiptap/vMarkdown/extensions/slash-command';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import { ProtocolsButton } from '@/components/ui/floating-ask-heidi/protocols-button';
import { SelectedTextBadge } from '@/components/ui/floating-ask-heidi/selected-text-badge';
import {
  useSelectedNoteTextContext,
  useSlashShortcutMenu,
} from '@/components/ui/floating-ask-heidi/use-selected-note-text-context';
import type { SuggestedChipCategory } from '@/components/ui/transcript-chips/types';
import { toast } from '@heidi/design-system/toast';
import {
  CHAT_ATTACHMENT_MAX_SIZE_BYTES,
  CHAT_ATTACHMENT_MAX_SIZE_MB,
  CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS,
  CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES,
} from '@heidi/utils';
import { useFileRejectionToast } from '@/hooks/fileUpload/useFileRejectionToast';
import { useQuerySessionById } from '@heidi/session/data';
import { useQueryUser } from '@heidi/workspace/data';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useEvidenceChipsConfig } from '@/hooks/useEvidenceChipsConfig';
import type {
  ChipOrigin,
  ChipSurface,
  MessageEntryMethod,
} from '@/models/analytics/features/chat';
import type { PersonalisedSourcesControlProps } from '@/models/analytics/features/evidence-shortcuts';

import { useIsMobile } from '@/hooks/useWindowDimensions';
import { trackChatModeToggled } from '@/lib/analytics/scribe/chat';
import { cn } from '@/lib/cn';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { isComposingEvent } from '@/utils/keyboardEventHelpers';
import { logger } from '@/utils/logger/logger';
import { PUBLIC_ROUTES } from '@/utils/publicRoutes';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  AlertTriangle,
  ArrowUp,
  Circle,
  Loader2,
  Paperclip,
  Square,
  UserRound,
} from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { Key } from 'ts-key-enum';
import { useChatSessionId } from '../hooks/useChatSessionId';
import { useContextLinkingAutocomplete } from '../hooks/useContextLinkingAutocomplete';
import { useSourceControl } from '../hooks/useSourceControl';
import { getFileExtension, isImageFile } from '@/utils/fileHelpers';
import { ChatInputFooter } from './ChatInputFooter';
import { ContextLinkingAutocomplete } from './ContextLinkingAutocomplete';
import { ContextLinkingButton } from './ContextLinkingButton';
import { LinkedContextBadge } from '@/components/ui/linked-context-badge';
import { chatInputMessages } from './ChatInput.messages';
import { formatSessionDate } from './ContextLinkingSessionsList';
import DictateButton from '@/components/ui/dictate-button';
import { ImageFileCard } from '@/components/ui/attachments/ImageFileCard';
import ModeToggleButton from './ModeToggleButton';
import { ResearchModeTooltip } from './ResearchModeTooltip';
import { SourcesButton } from './SourcesButton';
import { TypewriterPlaceholder } from './TypewriterPlaceholder';
import { InlineChatSuggestions } from './suggestions';

function getLinkedSessionSubtitle(
  linkedSession: { patientName?: string; createdAt?: string },
  intl: IntlShape
): string | undefined {
  if (linkedSession.patientName && linkedSession.createdAt) {
    return intl.formatMessage(chatInputMessages.linkedSessionSubtitleWithPatientAndDate, {
      patientName: linkedSession.patientName,
      sessionDate: formatSessionDate(linkedSession.createdAt, intl),
    });
  }
  return (
    linkedSession.patientName ??
    (linkedSession.createdAt ? formatSessionDate(linkedSession.createdAt, intl) : undefined)
  );
}

/**
 * Parses a suggestion preview string and renders [placeholder] segments
 * as styled badges. Kept local so Chat does not grow a K2 pair onto the
 * shared `composer-placeholder-slots` helper (that module is for composers
 * outside the Chat quarantine).
 */
function renderPreviewWithBadges(text: string): React.ReactNode {
  const parts = text.split(/(\[[^\]]+\])/g);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) => {
    if (part.startsWith('[') && part.endsWith(']')) {
      const label = part.slice(1, -1);
      return (
        <span
          key={index}
          className="rounded-sm bg-background-tertiary px-1.5 py-0.5 text-text-tertiary"
        >
          {label}
        </span>
      );
    }
    return part;
  });
}

// === Extracted Sub-Components to Reduce Duplication ===

interface SubmitButtonProps {
  isLoading: boolean;
  hasText: boolean;
  disabled?: boolean;
  onSend: () => void;
  onAbort?: () => void;
  isMobile?: boolean;
  isUploading?: boolean;
  hasUploadErrors?: boolean;
}

const SubmitButton = ({
  isLoading,
  hasText,
  disabled = false,
  onSend,
  onAbort,
  isMobile = false,
  isUploading = false,
  hasUploadErrors = false,
}: SubmitButtonProps) => {
  const intl = useIntl();

  const button = (
    <Button
    iconOnly
      disableAutomaticSvgSizing
      size={isMobile ? 'lg' : 'sm'}
      onClick={() => {
        if (isLoading) {
          onAbort?.();
        } else {
          onSend();
        }
      }}
      disabled={(!hasText && !isLoading) || disabled || isUploading || hasUploadErrors}
      className={cn(
        'group',
        isLoading && 'bg-background-button-primary-disabled hover:bg-background-button-primary-disabled'
      )}
      aria-label={
        isLoading
          ? intl.formatMessage(chatInputMessages.stopGenerating)
          : intl.formatMessage(chatInputMessages.submitMessage)
      }
    >
      {isLoading ? (
        <>
          <div className="absolute rotate-90 group-hover:hidden">
            <Circle size={11} strokeWidth={2.5} className="text-text-primary-invert/50" />
          </div>
          <Loader2
            size={12}
            strokeWidth={2.5}
            className="animate-spin text-text-primary-invert group-hover:hidden"
          />
          <Square size={12} strokeWidth={2.5} className="hidden group-hover:block" />
        </>
      ) : (
        <ArrowUp size={12} strokeWidth={2.5} />
      )}
    </Button>
  );

  // Show warning tooltip when there are upload errors blocking send
  if (hasUploadErrors && !isLoading) {
    return (
      <div className="flex items-center gap-1">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center">
                <AlertTriangle size={14} className="text-validation-destructive" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <FormattedMessage {...chatInputMessages.uploadErrorsBlockSend} />
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {button}
      </div>
    );
  }

  return button;
};

interface FilePreviewsProps {
  files: ChatUploadFile[];
  removeFile: (uid: string) => void;
  retryUpload: (uid: string) => void;
  className?: string;
  /** Translated "Failed" label for error file cards */
  failedLabel?: string;
  /** Translated "Too large" label for file size error cards */
  fileTooLargeLabel?: string;
  /** Translated "Not supported" label for unsupported-type error cards */
  unsupportedLabel?: string;
  /** Translated fallback error message for error file cards */
  fallbackErrorMessage?: string;
}

/**
 * Animated file previews with smooth layout transitions.
 *
 * Animation Strategy:
 * - Uses `layout` prop on each item for smooth repositioning when siblings are removed
 * - Exit animation: fade out only (no scale to avoid visual confusion with layout shifts)
 * - Layout uses spring animation for natural, smooth repositioning feel
 * - mode="popLayout" removes exiting items from flow so remaining items can animate
 *
 * Performance:
 * - Animations use opacity and transform (GPU-acceleratable)
 * - Spring animation provides smooth deceleration without jarring stops
 *
 * Accessibility:
 * - Respects prefers-reduced-motion via Framer Motion's automatic handling
 *
 * TODO: The layout animation is still jumpy when removing attachments. The remaining
 * items should slide smoothly to fill the gap, but currently they snap/jump. This may
 * be due to conflicts with the parent AnimatedAttachmentsRow height animation or CSS
 * layout issues. Needs further investigation.
 */
const FilePreviews = ({
  files,
  removeFile,
  retryUpload,
  className,
  failedLabel,
  fileTooLargeLabel,
  unsupportedLabel,
  fallbackErrorMessage,
}: FilePreviewsProps) => {
  if (files.length === 0) return null;

  return (
    <m.div layout className={cn('flex flex-wrap gap-2', className)}>
      <AnimatePresence mode="popLayout">
        {files.map((file) => (
          <m.div
            key={file.uid}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              // Opacity: quick fade for snappy feel
              opacity: { duration: FILE_EXIT_DURATION, ease: EASE_OUT_QUAD },
              // Layout: spring for smooth, natural repositioning
              // Stiffness 300 + damping 30 = responsive but not bouncy
              layout: {
                type: 'spring',
                stiffness: 300,
                damping: 30,
              },
            }}
          >
            {isImageFile(file) ? (
              <ImageFileCard
                file={file.file}
                onDelete={() => removeFile(file.uid)}
                onRetry={() => retryUpload(file.uid)}
                uploadStatus={file.uploadStatus}
                uploadProgress={getUploadProgress(file)}
                previewUrl={getPreviewUrl(file)}
                errorMessage={getErrorMessage(file)}
              />
            ) : (
              <FileCard
                variant={
                  isErrorFile(file)
                    ? 'error'
                    : isFileUploading(file)
                      ? 'loading'
                      : 'default'
                }
                loadingProgress={getUploadProgress(file)}
                fileName={file.name}
                fileType={getFileExtension(file.name)}
                errorMessage={getErrorMessage(file)}
                failedLabel={(() => {
                  const code = getErrorCode(file);
                  if (code === 'file_too_large') return fileTooLargeLabel;
                  if (code === 'unsupported_type') return unsupportedLabel;
                  return failedLabel;
                })()}
                fallbackErrorMessage={fallbackErrorMessage}
                onDelete={() => removeFile(file.uid)}
                onRetry={isErrorFile(file) ? () => retryUpload(file.uid) : undefined}
              />
            )}
          </m.div>
        ))}
      </AnimatePresence>
    </m.div>
  );
};

// =============================================================================
// Attachments Row Animation
// =============================================================================
//
// Animation Strategy:
// We use a two-phase sequenced animation when attachments are added/removed:
//
// EXPANDING (adding attachment):
//   1. Container height animates from 0 to auto (CONTAINER_DURATION)
//   2. After EXPAND_DELAY, badge content fades in with scale (CONTENT_DURATION)
//   Result: Container is ready before content appears - feels natural
//
// COLLAPSING (removing attachment):
//   1. Badge content fades out immediately (CONTENT_DURATION)
//   2. After content fades, container height animates to 0 (CONTAINER_DURATION)
//   Result: Content disappears before container shrinks - clean exit
//
// Performance Decisions:
// - Only animate `height` on container (not padding) to minimize layout recalcs
// - Padding applied via static CSS on inner wrapper
// - Badge animations use opacity + scale (GPU-acceleratable)
// - Height animation to `auto` requires Framer Motion measurement, but is
//   acceptable for this use case since it only triggers on user interaction
//
// Timing Values:
// - CONTAINER_DURATION: 0.22s - slightly longer for smooth height transition
// - CONTENT_DURATION: 0.15s - quick fade for snappy feel
// - EXPAND_DELAY: 0.15s - ensures container is mostly expanded before content shows
// - FILE_EXIT_DURATION: 0.2s - fade out duration for removed file attachments
// - File layout uses spring animation (stiffness: 300, damping: 30) for smooth repositioning
// =============================================================================

// Easing: ease-out-quad per animation guidelines - best for entering elements
const EASE_OUT_QUAD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

// Animation timing constants
const CONTAINER_DURATION = 0.22;
const CONTENT_DURATION = 0.15;
const EXPAND_DELAY = 0.15;
const FILE_EXIT_DURATION = 0.2; // Fade out duration for removed files

interface AnimatedAttachmentsRowProps {
  hasContent: boolean;
  children: React.ReactNode;
}

/**
 * Animated container for the attachments row (linked sessions/patients, files).
 * Handles smooth height transitions with sequenced timing.
 *
 * Children (badges) must handle their own enter/exit animations via AnimatePresence.
 * This component only manages the container height collapse/expand.
 */
const AnimatedAttachmentsRow = ({
  hasContent,
  children,
}: AnimatedAttachmentsRowProps) => {
  // Track previous state to determine if we're collapsing (for delayed container shrink)
  const wasContentVisible = useRef(hasContent);
  const isCollapsing = !hasContent && wasContentVisible.current;

  // Update ref after render
  useEffect(() => {
    wasContentVisible.current = hasContent;
  }, [hasContent]);

  return (
    <m.div
      className="overflow-hidden"
      initial={false}
      animate={{ height: hasContent ? 'auto' : 0 }}
      transition={{
        duration: CONTAINER_DURATION,
        ease: EASE_OUT_QUAD,
        // When collapsing, delay container shrink to let content fade out first
        delay: isCollapsing ? CONTENT_DURATION : 0,
      }}
    >
      {/* Static padding wrapper - avoids animating padding for better performance */}
      <div className="flex flex-wrap gap-2 px-3 pt-3">{children}</div>
    </m.div>
  );
};

interface ActionButtonsProps {
  disabled: boolean;
  isSessionless: boolean;
  isMobile: boolean;
  editor: Editor | null;
  isLoading: boolean;
  hasText: boolean;
  onSend: () => void;
  onAbort?: () => void;
  isUploading?: boolean;
  hasUploadErrors?: boolean;
}

const ActionButtons = ({
  disabled,
  isSessionless,
  isMobile,
  editor,
  isLoading,
  hasText,
  onSend,
  onAbort,
  isUploading,
  hasUploadErrors,
}: ActionButtonsProps) => {
  const router = useRouter();
  const isPublicRoute = PUBLIC_ROUTES.some((route) => router.pathname.startsWith(route));

  return <div className="flex shrink-0 gap-1">
    {!isPublicRoute && <DictateButton disabled={disabled} isMobile={isMobile} editor={editor} />}
    <SubmitButton
      isLoading={isLoading}
      hasText={hasText}
      disabled={disabled}
      onSend={onSend}
      onAbort={onAbort}
      isMobile={isMobile}
      isUploading={isUploading}
      hasUploadErrors={hasUploadErrors}
    />
  </div>
};

interface SourcesControlProps extends PersonalisedSourcesControlProps {
  disabled: boolean;
  isMobile: boolean;
  /** Whether to show the onboarding tooltip for source control. */
  enableOnboardingTooltip?: boolean;
  /** Whether to show the evidence onboarding sources spotlight for scribe users (step 2/3). */
  enableSourcesSpotlight?: boolean;
  /** Whether to show compact (icon-only) badges. Used for in-session view. */
  compactBadges?: boolean;
  /** Which side to show the popover on. Defaults to 'top'. */
  popoverSide?: 'top' | 'bottom';
  /** Content to render between the source button and badges (e.g., ProtocolsButton). */
  slotAfterButton?: React.ReactNode;
  /** Hide source badges entirely. */
  hideBadges?: boolean;
}

const SourcesControl = ({
  disabled,
  isMobile,
  enableOnboardingTooltip = false,
  enableSourcesSpotlight = false,
  compactBadges = false,
  popoverSide = 'top',
  slotAfterButton,
  hideBadges = false,
  personalisedSourceLogos = false,
  analyticsSurface,
}: SourcesControlProps) => (
  <SourcesButton
    isMobile={isMobile}
    enableOnboardingTooltip={enableOnboardingTooltip}
    enableSourcesSpotlight={enableSourcesSpotlight}
    compactBadges={compactBadges}
    popoverSide={popoverSide}
    disabled={disabled}
    slotAfterButton={slotAfterButton}
    hideBadges={hideBadges}
    personalisedSourceLogos={personalisedSourceLogos}
    analyticsSurface={analyticsSurface}
  />
);

interface ChatInputProps extends PersonalisedSourcesControlProps {
  onSend: (
    message: string,
    files?: File[], // Legacy: raw files (deprecated)
    attachmentIds?: string[], // Pre-uploaded attachment IDs
    fileMetadata?: Array<{
      name: string;
      type: string;
      url?: string;
      attachment_id?: string;
    }>,
    entryMethod?: MessageEntryMethod,
    chipLabel?: string,
    chipCategory?: string,
    chipOrigin?: ChipOrigin,
    chipSurface?: ChipSurface,
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  isLoading?: boolean;
  onAbort?: () => void;
  isSessionless?: boolean;
  /** Chat ID for draft persistence. Pass null/undefined for new chats. */
  chatId?: string | null;
  /** Whether to show the onboarding tooltip for source control. */
  enableOnboardingTooltip?: boolean;
  /** Whether to show the evidence onboarding sources spotlight (step 2/3). Sidebar only. */
  enableSourcesSpotlight?: boolean;
  /** Input state for dynamic placeholder - 'default' for homepage, 'follow up' for active conversations */
  inputState?: 'default' | 'follow up';
  /** Whether to show compact (icon-only) source badges. Used for in-session view. */
  compactBadges?: boolean;
  /** Hide source badges entirely. */
  hideBadges?: boolean;
  /** Which side to show the source control popover on. Use 'bottom' on homepage. */
  popoverSide?: 'top' | 'bottom';
  /** Whether to show inline suggestions below the input (alternative to popover). */
  showInlineSuggestions?: boolean;
  /** Direction inline suggestions extend from the input.
   *  - `'down'` (default): below the input (out-of-session homepage)
   *  - `'up'`: above the input (in-session sidebar) */
  suggestionsDirection?: 'down' | 'up';
  /** Whether context linking is locked (chat has started, can't change patient/session) */
  isContextLocked?: boolean;
  /** Whether to show the disclaimer below the input field */
  showDisclaimer?: boolean;
  /** Preview text to show in the input when hovering over a suggestion (ghost text) */
  suggestionPreview?: string | null;
  /** Starter text to populate the input with (from verb suggestion click). Consumed once, then cleared via onPopulateConsumed. */
  populateValue?: string | null;
  /** Called after populateValue has been consumed (inserted into editor) so parent can clear state. */
  onPopulateConsumed?: () => void;
  // Suggestions toggle props (for in-session use)
  /** Whether suggestions are enabled */
  suggestEnabled?: boolean;
  /** Callback when suggestions toggle changes */
  onSuggestEnabledChange?: (enabled: boolean) => void;
  /** Enabled suggestion categories */
  enabledCategories?: Set<SuggestedChipCategory>;
  /** Callback when a category is toggled */
  onCategoryToggle?: (category: SuggestedChipCategory) => void;
  /** When true, renders in full-view layout for mobile active state.
   *  InlineChatSuggestions are shown above the input as a scrollable area. */
  mobileFullView?: boolean;
  /** When true, shows the ambient glow while a response is streaming or recovering. */
  isGenerating?: boolean;
}

export const ChatInput = ({
  onSend,
  disabled: disabledProp = false,
  placeholder,
  autoFocus = false,
  isLoading = false,
  onAbort,
  isSessionless = false,
  chatId,
  enableOnboardingTooltip = false,
  enableSourcesSpotlight = false,
  inputState = 'default',
  compactBadges = false,
  hideBadges = false,
  popoverSide = 'top',
  showInlineSuggestions = false,
  suggestionsDirection = 'down',
  isContextLocked = false,
  showDisclaimer = false,
  suggestionPreview,
  populateValue,
  onPopulateConsumed,
  // Suggestions toggle props
  suggestEnabled,
  onSuggestEnabledChange,
  enabledCategories,
  onCategoryToggle,
  mobileFullView = false,
  personalisedSourceLogos = false,
  analyticsSurface,
  isGenerating = false,
}: ChatInputProps): JSX.Element => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  // Evidence chat needs connectivity, so disable the whole composer
  // (editor, attachments, send) while offline. No-op on web / online — the
  // gate only fires on desktop with the offline-recording flag on.
  const { isOfflineGated, gatedTooltip } = useOfflineGatedTooltip();
  const disabled = disabledProp || isOfflineGated;
  // Use chat-specific hook that only returns sessionId on session pages
  const sessionId = useChatSessionId() ?? '';
  const [newMode, setNewMode] = useAtom(newChatModeAtom);
  const [isDragOver, setIsDragOver] = useState(false);
  const [areSuggestionsVisible, setAreSuggestionsVisible] = useState(false);
  // Reset suggestion visibility when inline suggestions are unmounted (e.g. after first query)
  useEffect(() => {
    if (!showInlineSuggestions) {
      setAreSuggestionsVisible(false);
    }
  }, [showInlineSuggestions]);
  const dragCounterRef = useRef(0);
  const addFilesRef = useRef<(files: File[]) => void>();
  const handlePasteRef = useRef<(event: ClipboardEvent) => boolean>();
  const setEvidenceInputActive = useSetAtom(evidenceInputActiveAtom);
  const [isEditorFocused, setIsEditorFocused] = useState(false);

  // Context linking for out-of-session Evidence queries
  const [linkedSession, setLinkedSession] = useAtom(linkedSessionContextAtom);
  const [linkedPatient, setLinkedPatient] = useAtom(linkedPatientContextAtom);
  const linkedContextPreview = useAtomValue(linkedContextPreviewAtom);

  const isSessionPreviewActive =
    !!linkedSession &&
    linkedContextPreview?.type === 'session' &&
    linkedContextPreview.id === linkedSession.id;
  const isPatientPreviewActive =
    !!linkedPatient &&
    linkedContextPreview?.type === 'patient' &&
    linkedContextPreview.id === linkedPatient.id;
  const router = useRouter();
  const isPublicRoute = PUBLIC_ROUTES.some((route) => router.pathname.startsWith(route));
  const isContextLinkingEnabled = isSessionless && !isPublicRoute;
  const isFileAttachmentEnabled = IS_CHAT_ATTACHMENTS_ENABLED() && !isPublicRoute;
  const isFileOnly = !isSessionless;
  const { isInSessionEvidenceShown, isPending: isPendingEvidenceAccess } = useEvidenceAccess();
  const isPatientSessionLinkingEnabled =
    isContextLinkingEnabled && isInSessionEvidenceShown;
  const isContextButtonFileOnly =
    isFileOnly || (!isPendingEvidenceAccess && !isPatientSessionLinkingEnabled);

  const {
    pinnedChips,
    shortcutCount,
    maxShortcuts,
    isAtShortcutLimit,
    handleAddPermanentChip,
    handleEditPermanentChip,
    handleRemovePermanentChip,
    handleRestoreDefaultShortcut,
  } = useEvidenceChipsConfig();

  // Out-of-session with NO linked patient/session: only surface team
  // shortcuts (curated by team admins as plain query strings). Personal
  // pins and defaults (Prep, DDx, Questions, Plan) are hidden because
  // they may reference consultation context. Once a patient or session is
  // linked, show all shortcuts as if in-session (EVI-1406).
  const hasContextForProtocols = !isSessionless || linkedSession !== null || linkedPatient !== null;
  const shouldRestrictToTeamShortcuts = isSessionless && !hasContextForProtocols;
  const protocolsPinnedChips = useMemo(
    () =>
      shouldRestrictToTeamShortcuts
        ? pinnedChips.filter((c) => Boolean(c.teamShortcutId))
        : pinnedChips,
    [shouldRestrictToTeamShortcuts, pinnedChips]
  );

  // Disable the slash menu only when restricted AND no team shortcuts
  // are available to surface.
  const isProtocolsDisabledForLackOfContext =
    shouldRestrictToTeamShortcuts && protocolsPinnedChips.length === 0;
  const protocolsDisabledTooltip = gatedTooltip(
    isProtocolsDisabledForLackOfContext
      ? intl.formatMessage(chatInputMessages.protocolsDisabledTooltip)
      : undefined
  );
  // Mobile hides the button on public routes; desktop doesn't restrict it.
  const isProtocolsRendered = !isMobile || !isPublicRoute;
  const isSlashMenuAvailable =
    isProtocolsRendered &&
    !isProtocolsDisabledForLackOfContext &&
    !isOfflineGated &&
    !disabled;

  // Exclude the SlashCommand extension so the default slash menu never
  // conflicts with our "/" trigger.
  const excludedExtensions = useMemo(() => [SlashCommand], []);

  const effectiveMode = isSessionless ? ChatMode.EVIDENCE : newMode;

  const isSourcesFilterEnabled =
    effectiveMode === ChatMode.EVIDENCE || effectiveMode === ChatMode.AUTO;

  // Trial source control banner: show after 5 chats if user hasn't used source control yet
  // Get dynamic placeholder from source control hook
  const { placeholder: sourceControlPlaceholder } = useSourceControl({ inputState });

  // Determine if animated placeholder should be enabled
  const useAnimatedPlaceholder = isSourcesFilterEnabled;

  const defaultPlaceholder = placeholder ?? sourceControlPlaceholder;

  const { editor: chatEditor } = useBlockEditorMarkdown({
    content: '',
    editable: !disabled,
    placeholder: defaultPlaceholder,
    autofocus: autoFocus,
    showPlaceholderWhenDisabled: true,
    excludedExtensions,
    editorPropsOverride: IS_CHAT_ATTACHMENTS_ENABLED()
      ? {
          handleDrop: (_view: unknown, event: DragEvent) => {
            const files = event.dataTransfer?.files;
            if (files?.length) {
              event.preventDefault();
              // Stop propagation so the wrapping div's React onDrop doesn't
              // also fire and call addFiles a second time, which would create
              // a duplicate ChatUploadFile entry stuck in the uploading state.
              event.stopPropagation();
              dragCounterRef.current = 0;
              setIsDragOver(false);
              addFilesRef.current?.(Array.from(files));
              return true;
            }
            return false;
          },
          handlePaste: (_view: unknown, event: ClipboardEvent) =>
            handlePasteRef.current?.(event) ?? false,
        }
      : undefined,
  });

  // Draft persistence for text and files
  // Use chatId if available, otherwise fall back to sessionId for in-session chats
  const draftKey = chatId ?? (sessionId ? `session-${sessionId}` : null);
  const { draftFiles, setDraftFiles, clearDraft } = useChatInputDraft({
    chatId: draftKey,
    editor: chatEditor,
  });

  // Debounced toast notification when file uploads fail.
  // If multiple files fail in quick succession, collapses into a single "N
  // files failed" toast. Tracks each failure by its error code so the
  // composed toast picks the most-specific copy (rate limit, size limit,
  // unsupported type, or a generic upload failure).
  const uploadErrorBufferRef = useRef<
    { fileName: string; errorCode: ChatUploadErrorCode }[]
  >([]);
  const uploadErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { trackChatError } = useEvidenceAnalytics();
  const handleUploadError = useCallback(
    (fileName: string, errorMessage: string, errorCode: ChatUploadErrorCode) => {
      trackChatError({
        errorType: 'attachment',
        attachmentReason: errorCode,
        errorMessage,
      });
      finishTracking({
        key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
        metadata: {
          context_type: 'file',
          result: 'error',
          error_code: errorCode,
        },
      });

      uploadErrorBufferRef.current.push({ fileName, errorCode });

      // Clear any pending timer so we batch rapid failures together
      if (uploadErrorTimerRef.current) {
        clearTimeout(uploadErrorTimerRef.current);
      }

      uploadErrorTimerRef.current = setTimeout(() => {
        const failedEntries = uploadErrorBufferRef.current;
        uploadErrorBufferRef.current = [];
        uploadErrorTimerRef.current = null;

        if (failedEntries.length === 0) return;

        const codes = new Set(failedEntries.map((e) => e.errorCode));
        const allFileTooLarge = codes.size === 1 && codes.has('file_too_large');
        const allUnsupported = codes.size === 1 && codes.has('unsupported_type');
        const allRateLimited = codes.size === 1 && codes.has('rate_limited');

        if (allRateLimited) {
          toast({
            title: intl.formatMessage(chatInputMessages.uploadRateLimited),
            description: intl.formatMessage(
              chatInputMessages.uploadRateLimitedDescription
            ),
            tone: 'destructive',
          });
          return;
        }

        // Single file → name it. Multi file → count + most-specific bucket.
        if (failedEntries.length === 1 && failedEntries[0]) {
          const entry = failedEntries[0];
          if (entry.errorCode === 'file_too_large') {
            toast({
              title: intl.formatMessage(chatInputMessages.fileTooLarge),
              description: intl.formatMessage(chatInputMessages.fileTooLargeDescription, {
                fileName: entry.fileName,
                maxSizeMB: CHAT_ATTACHMENT_MAX_SIZE_MB,
              }),
              tone: 'destructive',
            });
          } else if (entry.errorCode === 'unsupported_type') {
            // Predictable type-rejection — the inline file card already
            // shows "Not supported", so a redundant toast just adds noise.
            // Skip it.
          } else {
            toast({
              title: intl.formatMessage(chatInputMessages.uploadFailed),
              description: intl.formatMessage(chatInputMessages.uploadFailedDescription, {
                fileName: entry.fileName,
              }),
              tone: 'destructive',
            });
          }
          return;
        }

        if (allFileTooLarge) {
          toast({
            title: intl.formatMessage(chatInputMessages.fileTooLarge),
            description: intl.formatMessage(chatInputMessages.filesTooLargeMultiple, {
              count: failedEntries.length,
              maxSizeMB: CHAT_ATTACHMENT_MAX_SIZE_MB,
            }),
            tone: 'destructive',
          });
        } else if (allUnsupported) {
          // Multiple unsupported files: still skip the toast — the file
          // cards collectively make the problem obvious.
        } else {
          toast({
            title: intl.formatMessage(chatInputMessages.uploadFailed),
            description: intl.formatMessage(chatInputMessages.uploadFailedMultiple, {
              count: failedEntries.length,
            }),
            tone: 'destructive',
          });
        }
      }, 500);
    },
    [intl, trackChatError]
  );

  // Cleanup debounce timer on unmount to prevent stale toast
  useEffect(() => {
    return () => {
      if (uploadErrorTimerRef.current) {
        clearTimeout(uploadErrorTimerRef.current);
      }
      uploadErrorBufferRef.current = [];
    };
  }, []);

  // File upload with draft persistence integration
  const {
    files,
    addFiles,
    removeFile,
    clearFiles,
    retryUpload,
    isUploading,
    hasErrors: hasUploadErrors,
    getAttachmentIds,
    getFileMetadata,
  } = useChatFileUpload({
    initialFiles: draftFiles,
    onFilesChange: setDraftFiles,
    onUploadError: handleUploadError,
    formatErrorMessage: (errorCode) =>
      formatChatUploadErrorMessage(intl, errorCode),
  });

  // Client-side rejection: stop unsupported / oversized files before the
  // upload pipeline ever starts. Audio files get a friendlier short-circuit
  // toast that explains the session flow is the right place for them.
  // The hook depends on individual primitive fields of this config rather
  // than the object reference, so passing a fresh literal each render is
  // fine — no `useMemo` needed.
  const { partitionAndReport } = useFileRejectionToast({
    allowedExtensions: CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS,
    allowedMimeTypes: CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES,
    maxBytes: CHAT_ATTACHMENT_MAX_SIZE_BYTES,
    allowedFormatsLabel: intl.formatMessage(
      chatInputMessages.attachmentsAllowedFormatsLabel
    ),
    audioShortCircuit: {
      title: intl.formatMessage(chatInputMessages.audioAttachmentTitle),
      description: intl.formatMessage(
        chatInputMessages.audioAttachmentDescription
      ),
    },
  });

  // Shared by both `FilePreviews` instances below (the docked row inside the
  // composer, and the floating row above it). Same labels every render.
  const filePreviewLabels = {
    failedLabel: intl.formatMessage(chatInputMessages.fileCardFailed),
    fileTooLargeLabel: intl.formatMessage(chatInputMessages.fileCardTooLarge),
    unsupportedLabel: intl.formatMessage(chatInputMessages.fileCardUnsupported),
    fallbackErrorMessage: intl.formatMessage(chatInputMessages.uploadFailed),
  };

  // All three entry points (file picker, drop, paste) funnel through this
  // wrapper so the rejection toast / audio short-circuit are uniform.
  const addFilesValidated = useCallback(
    (incoming: File[]) => {
      const accepted = partitionAndReport(incoming);
      if (accepted.length > 0) {
        addFiles(accepted);
      }
    },
    [addFiles, partitionAndReport]
  );

  addFilesRef.current = addFilesValidated;

  const previousAttachedContextRef = useRef({
    linkedSessionId: linkedSession?.id ?? null,
    linkedPatientId: linkedPatient?.id ?? null,
    filesCount: files.length,
  });

  useEffect(() => {
    const previous = previousAttachedContextRef.current;

    if (linkedSession?.id && previous.linkedSessionId !== linkedSession.id) {
      const raf = requestAnimationFrame(() => {
        finishTracking({
          key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
          metadata: {
            context_type: 'session',
            result: 'attached',
          },
        });
      });
      previousAttachedContextRef.current = {
        linkedSessionId: linkedSession.id,
        linkedPatientId: linkedPatient?.id ?? null,
        filesCount: files.length,
      };
      return () => cancelAnimationFrame(raf);
    }

    if (linkedPatient?.id && previous.linkedPatientId !== linkedPatient.id) {
      const raf = requestAnimationFrame(() => {
        finishTracking({
          key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
          metadata: {
            context_type: 'patient',
            result: 'attached',
          },
        });
      });
      previousAttachedContextRef.current = {
        linkedSessionId: linkedSession?.id ?? null,
        linkedPatientId: linkedPatient.id,
        filesCount: files.length,
      };
      return () => cancelAnimationFrame(raf);
    }

    if (files.length > previous.filesCount) {
      const raf = requestAnimationFrame(() => {
        finishTracking({
          key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
          metadata: {
            context_type: 'file',
            result: 'attached',
            file_count: files.length - previous.filesCount,
          },
        });
      });
      previousAttachedContextRef.current = {
        linkedSessionId: linkedSession?.id ?? null,
        linkedPatientId: linkedPatient?.id ?? null,
        filesCount: files.length,
      };
      return () => cancelAnimationFrame(raf);
    }

    previousAttachedContextRef.current = {
      linkedSessionId: linkedSession?.id ?? null,
      linkedPatientId: linkedPatient?.id ?? null,
      filesCount: files.length,
    };
  }, [linkedSession?.id, linkedPatient?.id, files.length]);

  const handleTrackedFilesAdded = useCallback(
    (selectedFiles: File[]) => {
      if (selectedFiles.length > 0) {
        startTracking({
          key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
          metadata: {
            context_type: 'file',
            source: 'file_picker',
            file_count: selectedFiles.length,
          },
        });
      }
      addFilesValidated(selectedFiles);
    },
    [addFilesValidated]
  );

  const handleFilesDetectedFromPaste = useCallback(
    (detectedFiles: File[]) => {
      if (detectedFiles.length > 0) {
        startTracking({
          key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
          metadata: {
            context_type: 'file',
            source: 'paste',
            file_count: detectedFiles.length,
          },
        });
      }
      addFilesValidated(detectedFiles);
    },
    [addFilesValidated]
  );

  // Handle paste events to attach files from clipboard
  const { handlePaste } = useChatPaste({
    onFilesDetected: handleFilesDetectedFromPaste,
    enabled: IS_CHAT_ATTACHMENTS_ENABLED(),
  });
  handlePasteRef.current = handlePaste;

  // Drag and drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current++;
      if (
        e.dataTransfer.types.includes('Files') &&
        !disabled &&
        IS_CHAT_ATTACHMENTS_ENABLED()
      ) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);

      if (disabled || !IS_CHAT_ATTACHMENTS_ENABLED()) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        startTracking({
          key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
          metadata: {
            context_type: 'file',
            source: 'drag_drop',
            file_count: droppedFiles.length,
          },
        });
        addFilesValidated(droppedFiles);
      }
    },
    [disabled, addFilesValidated]
  );

  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const dictateStatus = useAtomValue(sectionDictateStatusAtom);
  const selectedDomains = useAtomValue(selectedDomainsAtom);
  const setSelectedDomains = useSetAtom(setSelectedDomainsAtom);
  const { data: allowedDomains } = useQueryUser(
    (data) => data?.session_settings?.allowed_domains
  );

  const hasInitializedRef = useRef(false);

  // Initialize selectedDomainsAtom from allowedDomains when it becomes available
  // domainsAtom is now derived from selectedDomainsAtom, so no manual syncing needed
  useEffect(() => {
    if (hasInitializedRef.current) return;

    if (allowedDomains && allowedDomains.length > 0 && selectedDomains.size === 0) {
      setSelectedDomains(allowedDomains);
      hasInitializedRef.current = true;
    }
  }, [allowedDomains, selectedDomains.size, setSelectedDomains]);

  const handleModeChange = useCallback(
    (newModeValue: ChatMode) => {
      if (sessionId && effectiveMode !== newModeValue && !isSessionless) {
        trackChatModeToggled({
          sessionId,
          previous_mode: effectiveMode,
          new_mode: newModeValue,
        });
      }

      if (!isSessionless) {
        setNewMode(newModeValue);
      }
    },
    [sessionId, effectiveMode, isSessionless, setNewMode]
  );

  // Stop dictation when tab is no longer Ask Heidi v2 OR when dictation starts in another source
  useEffect(() => {
    // Only stop if Chat was the one dictating/processing
    const wasChatDictating =
      (dictateStatus.isDictating || dictateStatus.isProcessing) &&
      dictateStatus.tabName === 'askAi';

    if (!wasChatDictating || !chatEditor) {
      return; // Nothing to stop
    }

    const shouldStopDictation =
      sidebarOpenTab !== SidebarTab.ASK_HEIDI_V2 || // Sidebar tab changed away from Ask Heidi
      dictateStatus.tabName !== 'askAi'; // Dictation switched to a different source

    if (shouldStopDictation) {
      try {
        chatEditor.commands.stopSpeechRecognition();
      } catch (error) {
        logger.error('Failed to stop chat dictation', {
          error,
          sidebarOpenTab,
          dictateTabName: dictateStatus.tabName,
        });
      }
    }
  }, [
    sidebarOpenTab,
    dictateStatus.isDictating,
    dictateStatus.isProcessing,
    dictateStatus.tabName,
    chatEditor,
  ]);

  const hasText = useEditorState({
    editor: chatEditor,
    selector: ({ editor }) => {
      return editor.getText().trim().length > 0;
    },
  });

  // Track editor focus for mobile active state
  useEffect(() => {
    if (!chatEditor) return;

    setIsEditorFocused(chatEditor.isFocused);

    const handleFocus = () => setIsEditorFocused(true);
    const handleBlur = () => setIsEditorFocused(false);

    chatEditor.on('focus', handleFocus);
    chatEditor.on('blur', handleBlur);

    return () => {
      chatEditor.off('focus', handleFocus);
      chatEditor.off('blur', handleBlur);
    };
  }, [chatEditor]);

  // Sync evidenceInputActiveAtom for mobile sessionless layout switching
  useEffect(() => {
    if (isMobile && isSessionless) {
      setEvidenceInputActive(hasText || isEditorFocused);
    }
    return () => {
      // Reset when unmounting to avoid stale state
      if (isMobile && isSessionless) {
        setEvidenceInputActive(false);
      }
    };
  }, [isMobile, isSessionless, hasText, isEditorFocused, setEvidenceInputActive]);

  // Auto-focus when autoFocus prop is true
  useEffect(() => {
    if (autoFocus && chatEditor) {
      chatEditor.commands.focus('end');
    }
  }, [autoFocus, chatEditor]);

  // Focus trigger from external sources (e.g., clicking "Ask Heidi anything")
  const focusTrigger = useAtomValue(chatInputFocusTriggerAtom);
  useEffect(() => {
    if (focusTrigger > 0 && chatEditor) {
      chatEditor.commands.focus('end');
    }
  }, [focusTrigger, chatEditor]);

  // Populate input from verb suggestion click (starter text)
  useEffect(() => {
    if (populateValue != null && chatEditor) {
      if (populateValue) {
        chatEditor.chain().clearContent().insertContent(populateValue).run();
      }
      chatEditor.commands.focus('end');
      onPopulateConsumed?.();
    }
  }, [populateValue, chatEditor, onPopulateConsumed]);

  // Selection → Ask Heidi: highlighted note text attached as context. Mirrors
  // the floating bar's "Selected text" chip so the interaction is consistent
  // when the chat panel is already open while the user highlights note text.
  // Flag kill switch lives in useSelectedNoteTextContext (outside chat
  // quarantine) so ChatInput does not add a shrink-only flags outbound edge.
  const {
    hasSelectedTextContext,
    selectedNoteText,
    clearSelectedNoteText,
    setSelectedNoteText,
  } = useSelectedNoteTextContext({ sessionId });

  // Pending message from external sources (e.g., chip clicks, floating input)
  // Auto-sends when a pending message is set
  // Note: We don't check isLoading here - if streaming, the message will be queued
  const [pendingMessage, setPendingMessage] = useAtom(pendingChatMessageAtom);
  const [pendingEntryMethod, setPendingEntryMethod] = useAtom(pendingEntryMethodAtom);
  const [pendingChipMeta, setPendingChipMeta] = useAtom(pendingChipMetaAtom);
  const [pendingImmediate, setPendingImmediate] = useAtom(pendingChatImmediateAtom);
  const setScrollTrigger = useSetAtom(scrollToUserMessageTriggerAtom);
  useEffect(() => {
    if (pendingMessage && !disabled) {
      const entryMethod = pendingEntryMethod;
      const chipMeta = pendingChipMeta;
      const isImmediate = pendingImmediate;
      const send = () => {
        onSend(
          pendingMessage,
          undefined,
          undefined,
          undefined,
          entryMethod ?? undefined,
          chipMeta?.label,
          chipMeta?.category,
          chipMeta?.origin,
          chipMeta?.surface,
        );
        setPendingMessage(null);
        setPendingEntryMethod(null);
        setPendingChipMeta(null);
        setPendingImmediate(false);
        // `useAutoScroll` retries until a newer user message reaches the DOM.
        // A timer here would only postpone pinning that question.
        setScrollTrigger((prev) => prev + 1);
      };
      if (isImmediate) {
        send();
        return;
      }
      // Delay sending to ensure sidebar is fully open and rendered
      const timer = setTimeout(send, 500);
      return () => clearTimeout(timer);
    }
  }, [pendingMessage, pendingEntryMethod, pendingChipMeta, pendingImmediate, setPendingMessage, setPendingEntryMethod, setPendingChipMeta, setPendingImmediate, onSend, disabled, setScrollTrigger]);

  // Inline autocomplete for "#" (sessions) and "@" (patients) - like Slack/Linear/Cursor
  const contextLinkingAutocomplete = useContextLinkingAutocomplete({
    editor: chatEditor,
    enabled: isPatientSessionLinkingEnabled,
    isContextLocked,
    chatId,
  });
  const {
    selectSession: selectContextSession,
    selectPatient: selectContextPatient,
  } = contextLinkingAutocomplete;

  const handleSelectSessionContext = useCallback(
    (session: LinkedSessionContext) => {
      startTracking({
        key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
        metadata: {
          context_type: 'session',
          source: 'autocomplete',
        },
      });
      selectContextSession(session);
    },
    [selectContextSession]
  );

  const handleSelectPatientContext = useCallback(
    (patient: LinkedPatientContext) => {
      startTracking({
        key: TrackingKey.EVIDENCE_CONTEXT_ATTACH,
        metadata: {
          context_type: 'patient',
          source: 'autocomplete',
        },
      });
      selectContextPatient(patient);
    },
    [selectContextPatient]
  );

  // Guided "/" shortcuts menu — keep composer focus when the menu opens so
  // mouse/arrow interaction works in the full-window chat bar (EVI-2495).
  const {
    isGuidedEnabled,
    isOpen: isProtocolsMenuOpen,
    handleOpenChange: handleProtocolsMenuOpenChange,
    closeMenu: closeProtocolsMenu,
    shortcutQuery,
    protocolsRef,
    retainEditorFocus,
    handleSlashKeyDown,
    isSlashCommandActive,
  } = useSlashShortcutMenu({
    editor: chatEditor,
    enabled: isSlashMenuAvailable,
    pinnedChipsCount: protocolsPinnedChips.length,
  });

  // Handle protocol chip selection - send the query directly
  const handleProtocolChipClick = useCallback(
    (chip: {
      query: string;
      label?: string;
      category?: string;
      origin?: ChipOrigin;
    }) => {
      if (!chatEditor) return;
      // Clear the "/" and send the protocol query
      chatEditor.commands.clearContent();
      closeProtocolsMenu();
      onSend(
        chip.query,
        undefined,
        undefined,
        undefined,
        'shortcut_chip',
        chip.label,
        chip.category,
        chip.origin,
        'slash_menu',
      );
    },
    [chatEditor, onSend, closeProtocolsMenu]
  );

  // Helper to safely stop dictation before sending
  const stopDictationIfActive = useCallback(() => {
    if (!chatEditor) return;

    const isChatDictating =
      (dictateStatus.isDictating || dictateStatus.isProcessing) &&
      dictateStatus.tabName === 'askAi';

    if (isChatDictating) {
      try {
        chatEditor.commands.stopSpeechRecognition();
      } catch (error) {
        logger.error('Failed to stop dictation before sending chat message', { error });
      }
    }
  }, [
    chatEditor,
    dictateStatus.isDictating,
    dictateStatus.isProcessing,
    dictateStatus.tabName,
  ]);

  // Helper to clean up editor state after sending
  const cleanupAfterSend = useCallback(() => {
    if (!chatEditor) return;

    try {
      chatEditor.commands.clearContent();
    } catch (error) {
      logger.error('Failed to clear chat editor after sending', { error });
    }

    // Don't delete from server - files are now part of the message
    clearFiles(false);
    clearDraft();

    if (isMobile) {
      try {
        chatEditor.commands.blur();
      } catch (error) {
        logger.error('Failed to blur chat editor after sending', { error });
      }
    }
  }, [chatEditor, clearFiles, clearDraft, isMobile]);

  const handleSend = useCallback(() => {
    // Don't send while files are still uploading or if attachments have errors
    if (!chatEditor || isUploading || hasUploadErrors) return;

    try {
      const text = chatEditor.getText().trim();
      if (!text || disabled) return;

      stopDictationIfActive();

      const attachmentIds = getAttachmentIds();
      const fileMetadata = getFileMetadata();

      // With highlighted note text attached, scope the outgoing message to
      // that selection (same behaviour as the floating Ask Heidi bar). The
      // input keeps showing only what the user typed.
      const outgoing =
        hasSelectedTextContext && selectedNoteText
          ? composeSelectedTextQuestionPrompt(text, selectedNoteText.text)
          : text;

      onSend(
        outgoing,
        undefined,
        attachmentIds.length > 0 ? attachmentIds : undefined,
        fileMetadata.length > 0 ? fileMetadata : undefined
      );

      if (hasSelectedTextContext) setSelectedNoteText(null);

      cleanupAfterSend();
    } catch (error) {
      logger.error('Unexpected error in chat handleSend', { error });
    }
  }, [
    chatEditor,
    disabled,
    isUploading,
    hasUploadErrors,
    onSend,
    stopDictationIfActive,
    cleanupAfterSend,
    getAttachmentIds,
    getFileMetadata,
    hasSelectedTextContext,
    selectedNoteText,
    setSelectedNoteText,
  ]);

  const { data: sessionData } = useQuerySessionById({
    sessionId,
    select: (data) => ({
      name: data?.session_name || null,
      gist: data?.session_gist || null,
    }),
  });

  useHotkeys(
    Key.Enter,
    () => {
      handleSend();
    },
    {
      enabled:
        !disabled &&
        hasText &&
        !isUploading &&
        !hasUploadErrors &&
        !contextLinkingAutocomplete.isOpen &&
        !isSlashCommandActive,
      enableOnContentEditable: true,
      preventDefault: true,
      ignoreEventWhen: isComposingEvent,
    },
    [
      handleSend,
      disabled,
      hasText,
      isUploading,
      hasUploadErrors,
      contextLinkingAutocomplete.isOpen,
      isSlashCommandActive,
    ]
  );

  return (
    <div className={cn('group/chatinput relative w-full', mobileFullView && 'flex flex-1 flex-col')}>
      {/* Full-view suggestions - rendered above input on mobile active state */}
      {showInlineSuggestions && mobileFullView && (
        <InlineChatSuggestions
          editor={chatEditor}
          disabled={disabled || isLoading}
          onSend={(message) => onSend(message)}
          onVisibilityChange={setAreSuggestionsVisible}
          forceSearchText={populateValue}
          fullView
        />
      )}

      {/* APP-6672: when offline-gated, the composer below is greyed + inert; the
          Tooltip explains why on hover. Tooltip stays closed (open={false}) when
          online so it never interferes with normal use. */}
      <Tooltip open={isOfflineGated ? undefined : false}>
      <TooltipTrigger asChild>
      <div
        className={cn(
          'relative w-full border border-border bg-surface transition-all duration-200 ease-in-out',
          areSuggestionsVisible && !mobileFullView
            ? suggestionsDirection === 'up'
              ? 'rounded-b-xl rounded-t-none'
              : 'rounded-b-none rounded-t-xl'
            : 'rounded-xl',
          'group-focus-within/chatinput:border-border-pressed',
          // No outer padding - each row handles its own padding for both mobile and desktop
          !isDragOver && 'hover:border-border-hover',
          isDragOver && 'border-border-focus',
          mobileFullView && 'shrink-0',
          // APP-6672: Evidence chat needs connectivity — grey the whole composer
          // offline so the inert state is visible, not silent.
          isOfflineGated && 'cursor-not-allowed opacity-50',
        )}
        data-testid={isOfflineGated ? 'chat-input-offline-gated' : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDown={handleSlashKeyDown}
      >
        <ComposerAmbientGlow visible={isGenerating} testId="chat-input-ambient-glow" />
        {/* Inline autocomplete for "#" (sessions) and "@" (patients) - like Slack/Linear */}
        {isPatientSessionLinkingEnabled && (
          <ContextLinkingAutocomplete
            isOpen={contextLinkingAutocomplete.isOpen}
            type={contextLinkingAutocomplete.type}
            searchQuery={contextLinkingAutocomplete.searchQuery}
            onSelectSession={handleSelectSessionContext}
            onSelectPatient={handleSelectPatientContext}
            onClose={contextLinkingAutocomplete.close}
          />
        )}
        {/* Drag and drop overlay */}
        <AnimatePresence>
          {isDragOver && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="pointer-events-none absolute inset-0 z-50 overflow-hidden rounded-xl"
            >
              {/* Gradient background */}
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-gradient-to-r from-surface-accent/50 via-surface to-surface-accent/50 backdrop-blur-[1px]"
              />

              {/* Animated border effect */}
              <m.div
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="absolute inset-[2px] rounded-[10px] border-2 border-dashed border-border-focus"
              />

              {/* Center content - horizontal compact layout */}
              <m.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="absolute inset-0 flex items-center justify-center gap-3"
              >
                {/* Animated icon */}
                <m.div
                  initial={{ scale: 0.8, opacity: 0, x: -4 }}
                  animate={{ scale: 1, opacity: 1, x: 0 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{
                    duration: 0.25,
                    ease: [0.23, 1, 0.32, 1],
                  }}
                  className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-accent"
                >
                  <m.div
                    animate={{ y: [0, -2, 0] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <Paperclip className="size-4 text-text-primary" />
                  </m.div>
                </m.div>

                {/* Text */}
                <m.p
                  initial={{ opacity: 0, x: 4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, delay: 0.05 }}
                  className="text-sm font-medium text-text-primary"
                >
                  <FormattedMessage {...chatInputMessages.dropFilesHere} />
                </m.p>
              </m.div>
            </m.div>
          )}
        </AnimatePresence>
        {isMobile ? (
          // Mobile: Flex column layout matching Figma structure
          // Row 1: Attachments (inside container, at top)
          // Row 2: Input text area
          // Row 3: Controls - [add, sources] on left, [mic, send] on right
          <div className="flex w-full flex-col">
            {/* Row 1: Attachments - linked context badges and files (horizontal scroll on mobile) */}
            {((isPatientSessionLinkingEnabled && (linkedSession || linkedPatient)) ||
              files.length > 0) && (
              <div className="flex gap-2 overflow-x-auto px-3 pt-3 scrollbar-none">
                {/* Linked session badge */}
                {isPatientSessionLinkingEnabled && linkedSession && (
                  <div className="shrink-0">
                    <LinkedContextBadge
                      type="session"
                      name={linkedSession.name}
                      subtitle={getLinkedSessionSubtitle(linkedSession, intl)}
                      onRemove={() => setLinkedSession(null)}
                      isContextLocked={isContextLocked}
                      isActive={isSessionPreviewActive}
                    />
                  </div>
                )}
                {/* Linked patient badge */}
                {isPatientSessionLinkingEnabled && linkedPatient && (
                  <div className="shrink-0">
                    <LinkedContextBadge
                      type="patient"
                      name={linkedPatient.name}
                      subtitle={linkedPatient.demographicString}
                      onRemove={() => setLinkedPatient(null)}
                      isContextLocked={isContextLocked}
                      isActive={isPatientPreviewActive}
                    />
                  </div>
                )}
                {/* File previews */}
                <FilePreviews
                  files={files}
                  removeFile={removeFile}
                  retryUpload={retryUpload}
                  {...filePreviewLabels}
                  className="shrink-0"
                />
              </div>
            )}

            {/* Slide context chip - shown when presentation editor is open */}
            <SlideContextChip />

            {/* Selection → Ask Heidi: highlighted note text attached as context */}
            {hasSelectedTextContext && (
              <div className="flex px-3 pt-3">
                <SelectedTextBadge onRemove={clearSelectedNoteText} />
              </div>
            )}

            {/* Row 2: Input text area */}
            <div className="relative px-4 pb-2 pt-4">
              {chatEditor ? (
                <>
                  <BlockEditorMarkdown
                    testTag="chat-block-editor"
                    editor={chatEditor}
                    enableFindReplace={false}
                    enableSnippet={false}
                    enableBubbleMenu={false}
                    tabType={undefined}
                    editorType={EditorType.CONTEXT}
                    className={cn(
                      '[&_.ProseMirror]:max-h-[200px] [&_.ProseMirror]:min-h-[21px] [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:break-words [&_.ProseMirror]:font-normal',
                      (useAnimatedPlaceholder || (suggestionPreview && !hasText)) && '[&_.is-editor-empty::before]:!opacity-0',
                      suggestionPreview && hasText && '[&_.ProseMirror]:text-transparent [&_.ProseMirror]:caret-transparent'
                    )}
                  />
                  {/* Animated placeholder overlay - shown when editor is empty and animation is enabled */}
                  {useAnimatedPlaceholder && !hasText && !suggestionPreview && (
                    <div className="pointer-events-none absolute inset-x-4 bottom-2 top-4 flex items-start">
                      <TypewriterPlaceholder
                        placeholder={defaultPlaceholder}
                        className="min-h-[21px] text-sm"
                      />
                    </div>
                  )}
                  {/* Suggestion preview overlay - shown when hovering over a suggestion item */}
                  {suggestionPreview && (
                    <div
                      className="pointer-events-none absolute inset-x-4 bottom-2 top-4 flex items-start"
                      aria-hidden="true"
                    >
                      <span className="min-h-[21px] truncate text-sm text-text-tertiary transition-opacity duration-200 ease-out">
                        {renderPreviewWithBadges(suggestionPreview)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className={cn('min-h-[21px] text-sm', disabled && 'cursor-not-allowed')}
                >
                  {defaultPlaceholder}
                </div>
              )}
            </div>

            {/* Row 3: Controls - [add, protocols, sources, badges, suggestions, mode] on left, [mic, send] on right */}
            <div className="flex items-center justify-between px-3 pb-3 pt-2">
              <div className="flex items-center gap-1">
                {/* Context linking (add) button */}
                {isFileAttachmentEnabled && (
                  <ContextLinkingButton
                    isMobile={true}
                    disabled={disabled}
                    isContextLocked={isContextLocked}
                    onFilesAdded={handleTrackedFilesAdded}
                    fileOnly={isContextButtonFileOnly}
                    chatId={chatId}
                  />
                )}
                {/* Protocols button - between add and sources */}
                {isProtocolsRendered && (
                  <ProtocolsButton
                    ref={protocolsRef}
                    pinnedChips={protocolsPinnedChips}
                    onPinnedChipClick={handleProtocolChipClick}
                    disabled={
                      disabled || isProtocolsDisabledForLackOfContext || isOfflineGated
                    }
                    disabledTooltip={protocolsDisabledTooltip}
                    open={isProtocolsMenuOpen}
                    onOpenChange={handleProtocolsMenuOpenChange}
                    onAddProtocol={handleAddPermanentChip}
                    onEditProtocol={handleEditPermanentChip}
                    onRemoveProtocol={handleRemovePermanentChip}
                    onRestoreDefaultShortcut={handleRestoreDefaultShortcut}
                    isAtShortcutLimit={isAtShortcutLimit}
                    shortcutCount={shortcutCount}
                    maxShortcuts={maxShortcuts}
                    hideSystemProtocols={shouldRestrictToTeamShortcuts}
                    searchQuery={isGuidedEnabled ? shortcutQuery : undefined}
                    onRetainInputFocus={
                      isGuidedEnabled ? retainEditorFocus : undefined
                    }
                  />
                )}
                {/* Sources filter */}
                {isSourcesFilterEnabled && (
                  <SourcesControl
                    disabled={disabled}
                    isMobile={true}
                    enableOnboardingTooltip={enableOnboardingTooltip}
                    enableSourcesSpotlight={enableSourcesSpotlight}
                    compactBadges={compactBadges}
                    hideBadges={hideBadges}
                    popoverSide={popoverSide}
                    personalisedSourceLogos={personalisedSourceLogos}
                    analyticsSurface={analyticsSurface}
                  />
                )}
                {/* Mode toggle - hidden for sessionless users and when auto-mode-only flag is enabled */}
                {!isSessionless && !IS_AUTO_MODE_ONLY_ENABLED() && (
                  <ModeToggleButton
                    mode={effectiveMode}
                    onModeChange={handleModeChange}
                    disabled={disabled}
                    isMobile={true}
                    hideText={true}
                  />
                )}
              </div>

              <ActionButtons
                disabled={disabled}
                isSessionless={isSessionless}
                isMobile={true}
                editor={chatEditor}
                isLoading={isLoading}
                hasText={hasText}
                onSend={handleSend}
                onAbort={onAbort}
                isUploading={isUploading}
                hasUploadErrors={hasUploadErrors}
              />
            </div>
          </div>
        ) : (
          // Desktop: Three-level layout matching Figma structure
          // Each row has its own padding (no outer container padding)
          <div className="flex w-full flex-col">
            {/* Row 1: Attachments (session badge, linked context, file previews) */}
            {/* Animated container that smoothly expands/collapses */}
            <AnimatedAttachmentsRow
              hasContent={
                !!sessionData?.name ||
                (isPatientSessionLinkingEnabled && !!(linkedSession || linkedPatient)) ||
                files.length > 0
              }
            >
              {/* Session context badge */}
              {sessionData?.name && (
                <div className="flex">
                  <div className="inline-flex cursor-default items-center gap-2 rounded-lg border border-border bg-surface py-1 pl-1 pr-2">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background-tertiary">
                      <UserRound size={20} className="text-text-secondary" />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1.5">
                      {sessionData.gist && (
                        <TypographyCaptionBold className="truncate leading-none text-text-primary">
                          {sessionData.gist}
                        </TypographyCaptionBold>
                      )}
                      <TypographyFootNote className="truncate leading-none text-text-secondary">
                        {sessionData.name}
                      </TypographyFootNote>
                    </div>
                  </div>
                </div>
              )}

              {/* 
                Linked context badges with sequenced animations:
                - Enter: delayed fade+scale after container expands (EXPAND_DELAY)
                - Exit: immediate fade+scale, then container collapses
                AnimatePresence must be always-rendered (not conditional) to track exits
              */}
              <AnimatePresence>
                {isPatientSessionLinkingEnabled && linkedSession && (
                  <m.div
                    key="linked-session-badge"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: CONTENT_DURATION,
                        ease: EASE_OUT_QUAD,
                        delay: EXPAND_DELAY, // Wait for container to expand first
                      },
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.95,
                      transition: {
                        duration: CONTENT_DURATION,
                        ease: EASE_OUT_QUAD,
                      },
                    }}
                  >
                    <LinkedContextBadge
                      type="session"
                      name={linkedSession.name}
                      subtitle={getLinkedSessionSubtitle(linkedSession, intl)}
                      onRemove={() => setLinkedSession(null)}
                      isContextLocked={isContextLocked}
                      isActive={isSessionPreviewActive}
                    />
                  </m.div>
                )}
                {isPatientSessionLinkingEnabled && linkedPatient && (
                  <m.div
                    key="linked-patient-badge"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: CONTENT_DURATION,
                        ease: EASE_OUT_QUAD,
                        delay: EXPAND_DELAY, // Wait for container to expand first
                      },
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.95,
                      transition: {
                        duration: CONTENT_DURATION,
                        ease: EASE_OUT_QUAD,
                      },
                    }}
                  >
                    <LinkedContextBadge
                      type="patient"
                      name={linkedPatient.name}
                      subtitle={linkedPatient.demographicString}
                      onRemove={() => setLinkedPatient(null)}
                      isContextLocked={isContextLocked}
                      isActive={isPatientPreviewActive}
                    />
                  </m.div>
                )}
              </AnimatePresence>

              {/* File previews */}
              {files.length > 0 && (
                <FilePreviews
                  files={files}
                  removeFile={removeFile}
                  retryUpload={retryUpload}
                  {...filePreviewLabels}
                />
              )}
            </AnimatedAttachmentsRow>

            {/* Slide context chip - shown when presentation editor is open */}
            <SlideContextChip />

            {/* Selection → Ask Heidi: highlighted note text attached as context */}
            {hasSelectedTextContext && (
              <div className="flex px-4 pt-3">
                <SelectedTextBadge onRemove={clearSelectedNoteText} />
              </div>
            )}

            {/* Row 2: Input wrapper - pt-4 px-4 pb-2 */}
            <div className="relative px-4 pb-2 pt-4">
              {chatEditor ? (
                <>
                  <BlockEditorMarkdown
                    testTag="chat-block-editor"
                    editor={chatEditor}
                    enableFindReplace={false}
                    enableSnippet={false}
                    enableBubbleMenu={false}
                    tabType={undefined}
                    editorType={EditorType.CONTEXT}
                    className={cn(
                      'justify-center [&_.ProseMirror]:max-h-60 [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:font-normal',
                      (useAnimatedPlaceholder || (suggestionPreview && !hasText)) && '[&_.is-editor-empty::before]:!opacity-0',
                      suggestionPreview && hasText && '[&_.ProseMirror]:text-transparent [&_.ProseMirror]:caret-transparent'
                    )}
                  />
                  {/* Animated placeholder overlay - shown when editor is empty and animation is enabled */}
                  {/* Position matches parent padding: left-4 right-4 (px-4), top-4 (pt-4), bottom-2 (pb-2) */}
                  {useAnimatedPlaceholder && !hasText && !suggestionPreview && (
                    <div className="pointer-events-none absolute inset-x-4 bottom-2 top-4 flex items-start">
                      <TypewriterPlaceholder
                        placeholder={defaultPlaceholder}
                        className="text-sm leading-5"
                      />
                    </div>
                  )}
                  {/* Suggestion preview overlay - shown when hovering over a suggestion item */}
                  {suggestionPreview && (
                    <div
                      className="pointer-events-none absolute inset-x-4 bottom-2 top-4 flex items-start"
                      aria-hidden="true"
                    >
                      <span className="truncate text-sm leading-5 text-text-tertiary transition-opacity duration-200 ease-out">
                        {renderPreviewWithBadges(suggestionPreview)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className={cn(
                    'w-full resize-none overflow-auto break-words text-sm leading-5 text-text-primary outline-none',
                    'max-h-64',
                    disabled && 'cursor-not-allowed'
                  )}
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {defaultPlaceholder}
                </div>
              )}
            </div>

            {/* Row 3: Controls wrapper - pt-2 px-3 pb-3 */}
            <div className="flex items-center justify-between gap-3 px-3 pb-3 pt-2">
              <div className="flex items-center gap-1">
                {/* Mode toggle - hidden for sessionless users and when auto-mode-only flag is enabled */}
                {!isSessionless && !IS_AUTO_MODE_ONLY_ENABLED() && (
                  <ResearchModeTooltip>
                    <ModeToggleButton
                      mode={effectiveMode}
                      onModeChange={handleModeChange}
                      disabled={disabled}
                      isMobile={false}
                      hideText={false}
                    />
                  </ResearchModeTooltip>
                )}
                {isFileAttachmentEnabled && (
                  <ContextLinkingButton
                    isMobile={false}
                    disabled={disabled}
                    isContextLocked={isContextLocked}
                    onFilesAdded={handleTrackedFilesAdded}
                    fileOnly={isContextButtonFileOnly}
                    chatId={chatId}
                  />
                )}
                {/* Protocols button - between add and sources */}
                {isProtocolsRendered && (
                  <ProtocolsButton
                    ref={protocolsRef}
                    pinnedChips={protocolsPinnedChips}
                    onPinnedChipClick={handleProtocolChipClick}
                    disabled={
                      disabled || isProtocolsDisabledForLackOfContext || isOfflineGated
                    }
                    disabledTooltip={protocolsDisabledTooltip}
                    open={isProtocolsMenuOpen}
                    onOpenChange={handleProtocolsMenuOpenChange}
                    onAddProtocol={handleAddPermanentChip}
                    onEditProtocol={handleEditPermanentChip}
                    onRemoveProtocol={handleRemovePermanentChip}
                    onRestoreDefaultShortcut={handleRestoreDefaultShortcut}
                    isAtShortcutLimit={isAtShortcutLimit}
                    shortcutCount={shortcutCount}
                    maxShortcuts={maxShortcuts}
                    hideSystemProtocols={shouldRestrictToTeamShortcuts}
                    searchQuery={isGuidedEnabled ? shortcutQuery : undefined}
                    onRetainInputFocus={
                      isGuidedEnabled ? retainEditorFocus : undefined
                    }
                  />
                )}
                {/* Sources filter */}
                {isSourcesFilterEnabled && (
                  <SourcesControl
                    disabled={disabled}
                    isMobile={false}
                    enableOnboardingTooltip={enableOnboardingTooltip}
                    enableSourcesSpotlight={enableSourcesSpotlight}
                    compactBadges={compactBadges}
                    hideBadges={hideBadges}
                    popoverSide={popoverSide}
                    personalisedSourceLogos={personalisedSourceLogos}
                    analyticsSurface={analyticsSurface}
                  />
                )}
              </div>

              <ActionButtons
                disabled={disabled}
                isSessionless={isSessionless}
                isMobile={false}
                editor={chatEditor}
                isLoading={isLoading}
                hasText={hasText}
                onSend={handleSend}
                onAbort={onAbort}
                isUploading={isUploading}
                hasUploadErrors={hasUploadErrors}
              />
            </div>
          </div>
        )}

      </div>
      </TooltipTrigger>
      {isOfflineGated && (
        <TooltipContent>
          <FormattedMessage {...offlineGatingMessages.notAvailableOffline} />
          <TooltipArrow />
        </TooltipContent>
      )}
      </Tooltip>

      {/* Inline suggestions - overlays content adjacent to the input, does not push layout */}
      {/* Skipped when mobileFullView is active — full-view suggestions are rendered above instead */}
      {showInlineSuggestions && !mobileFullView && (
        <InlineChatSuggestions
          editor={chatEditor}
          disabled={disabled || isLoading}
          onSend={(message) => onSend(message)}
          onVisibilityChange={setAreSuggestionsVisible}
          forceSearchText={populateValue}
          direction={suggestionsDirection}
        />
      )}

      {/* Disclaimer + quota badge - shown for evidence experiences */}
      {showDisclaimer && <ChatInputFooter isSessionless={isSessionless} />}
    </div>
  );
};
